import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Trophy, Check, Lock, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function EventRegistrationPage() {
  const { eventCode } = useParams();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessVerified, setAccessVerified] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [registered, setRegistered] = useState(false);
  const [regResult, setRegResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    role: "Leader",
    competition_id: "",
    country: "",
    phone: "",
    phoneCode: "+30",
  });

  useEffect(() => {
    fetchEventInfo();
  }, [eventCode]);

  const fetchEventInfo = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventCode}/register`);
      setEventData(res.data);
      if (!res.data.requires_access_code) {
        setAccessVerified(true);
      }
      if (res.data.competitions?.length === 1) {
        setForm(prev => ({ ...prev, competition_id: res.data.competitions[0].competition_id }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Event not found or registration not available");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    try {
      await axios.post(`${API}/events/${eventCode}/verify-code`, { access_code: accessCode });
      setAccessVerified(true);
      toast.success("Access verified!");
    } catch {
      toast.error("Invalid access code");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter your name"); return; }
    if (!form.competition_id) { toast.error("Please select a competition"); return; }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${eventCode}/register`, {
        name: form.name.trim(),
        role: form.role,
        competition_id: form.competition_id,
        country: form.country.trim(),
        phone: form.phone ? `${form.phoneCode}${form.phone.trim()}` : "",
        access_code: accessCode || null,
      });
      setRegResult(res.data);
      setRegistered(true);
      toast.success("Registration successful!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Users className="w-16 h-16 text-[#27272a] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Registration Unavailable</h1>
          <p className="text-[#52525B]">{error}</p>
        </div>
      </div>
    );
  }

  if (registered && regResult) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <motion.div className="text-center max-w-md" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Registration Complete!</h1>
          <p className="text-[#A1A1AA] mb-6">{regResult.message}</p>
          <div className="glass p-6 rounded-xl">
            <p className="text-[#A1A1AA] text-sm mb-1">Your Bib Number</p>
            <p className="text-[#FFD700] text-5xl font-bold font-mono">#{regResult.bib_number}</p>
          </div>
          <p className="text-[#52525B] text-sm mt-6">Remember your number. Good luck!</p>
        </motion.div>
      </div>
    );
  }

  // Access code gate
  if (!accessVerified) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="max-w-sm w-full">
          <div className="text-center mb-8">
            {eventData?.logo_base64 && (
              <img src={eventData.logo_base64} alt="Event logo" className="max-h-16 mx-auto mb-4 rounded-lg" />
            )}
            <Lock className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
            <h1 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-2">{eventData?.festival_name}</h1>
            <p className="text-[#A1A1AA]">Enter the access code to register</p>
          </div>
          <div className="space-y-4">
            <Input
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Access Code"
              className="bg-black/20 border-white/10 text-white text-center text-lg"
              data-testid="access-code-input"
            />
            <Button onClick={handleVerifyCode} className="w-full btn-gold py-3" data-testid="verify-code-btn">
              Verify & Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          {eventData?.logo_base64 && (
            <img src={eventData.logo_base64} alt="Event logo" className="max-h-20 mx-auto mb-4 rounded-lg" />
          )}
          <img src="/jj-logo.png" alt="JackAndJillScore" className="w-24 h-24 mx-auto mb-4" />
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-1">{eventData?.festival_name}</h1>
          <p className="text-[#A1A1AA] text-sm">{eventData?.date} - {eventData?.location}</p>
        </div>

        <div className="glass p-6 rounded-xl">
          <h2 className="text-white font-bold text-lg mb-4">Dancer Registration</h2>
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Full Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                className="mt-1 bg-black/20 border-white/10 text-white"
                required
                data-testid="reg-name-input"
              />
            </div>

            <div>
              <Label className="text-[#A1A1AA]">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1 bg-black/20 border-white/10 text-white" data-testid="reg-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10">
                  <SelectItem value="Leader">Leader</SelectItem>
                  <SelectItem value="Follower">Follower</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#A1A1AA]">Country</Label>
              <Input
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                placeholder="e.g. Greece"
                className="mt-1 bg-black/20 border-white/10 text-white"
                data-testid="reg-country-input"
              />
            </div>

            <div>
              <Label className="text-[#A1A1AA]">Phone Number</Label>
              <div className="flex gap-2 mt-1">
                <select
                  value={form.phoneCode || "+30"}
                  onChange={(e) => setForm({ ...form, phoneCode: e.target.value })}
                  className="w-28 bg-black/20 border border-white/10 text-white rounded-md px-2 py-2 text-sm"
                  data-testid="reg-phone-code-select"
                >
                  <option value="+30">🇬🇷 +30</option>
                  <option value="+34">🇪🇸 +34</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+39">🇮🇹 +39</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+351">🇵🇹 +351</option>
                  <option value="+31">🇳🇱 +31</option>
                  <option value="+32">🇧🇪 +32</option>
                  <option value="+41">🇨🇭 +41</option>
                  <option value="+43">🇦🇹 +43</option>
                  <option value="+48">🇵🇱 +48</option>
                  <option value="+45">🇩🇰 +45</option>
                  <option value="+46">🇸🇪 +46</option>
                  <option value="+47">🇳🇴 +47</option>
                  <option value="+7">🇷🇺 +7</option>
                  <option value="+55">🇧🇷 +55</option>
                  <option value="+52">🇲🇽 +52</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+81">🇯🇵 +81</option>
                  <option value="+90">🇹🇷 +90</option>
                  <option value="+357">🇨🇾 +357</option>
                  <option value="+380">🇺🇦 +380</option>
                  <option value="+972">🇮🇱 +972</option>
                </select>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="6912345678"
                  type="tel"
                  className="flex-1 bg-black/20 border-white/10 text-white"
                  data-testid="reg-phone-input"
                />
              </div>
            </div>

            {eventData?.competitions?.length > 1 && (
              <div>
                <Label className="text-[#A1A1AA]">Competition</Label>
                <Select value={form.competition_id} onValueChange={(v) => setForm({ ...form, competition_id: v })}>
                  <SelectTrigger className="mt-1 bg-black/20 border-white/10 text-white" data-testid="reg-comp-select">
                    <SelectValue placeholder="Select competition" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-white/10">
                    {eventData.competitions.map(c => (
                      <SelectItem key={c.competition_id} value={c.competition_id}>{c.category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {eventData?.competitions?.length === 1 && (
              <div className="p-3 bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg">
                <p className="text-[#A1A1AA] text-xs">Competition</p>
                <p className="text-[#FFD700] font-medium">{eventData.competitions[0].category}</p>
              </div>
            )}

            <Button type="submit" className="w-full btn-gold py-3 text-lg" disabled={submitting} data-testid="reg-submit-btn">
              {submitting ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : "Register"}
            </Button>
          </form>
        </div>

        <footer className="text-center mt-6">
          <p className="text-[#27272a] text-xs">JackAndJillScore</p>
        </footer>
      </div>
    </div>
  );
}
